import { format } from 'date-fns';
import groupBy from 'ramda/src/groupBy';
import { useMemo } from 'react';
import type { MKEERAApiResponseItem } from '../dashboard.types';
import { excelSerialToDate } from '../utils/dataProcessing';

interface MilwaukeeansServedData {
  total: number;
  percentChange: number;
  monthLabels: string[];
  chartCounts: number[];
}

export function useMilwaukeeansServedChartData(data: MKEERAApiResponseItem[], totalRecords?: number): MilwaukeeansServedData {
  return useMemo(() => {
    // Safety check: ensure data is an array
    if (!Array.isArray(data) || data.length === 0) {
      return {
        total: 0,
        percentChange: 0,
        monthLabels: [],
        chartCounts: [],
      };
    }

    // Group by 'YYYY-MM' from dateOfPayment using Excel date conversion
    const grouped = groupBy((item: MKEERAApiResponseItem) => {
      try {
        let date: Date;
        const dateString = item.applicationData.dateOfPayment;

        // Check if it's an Excel serial number (numeric string)
        if (/^\d+$/.test(dateString)) {
          date = excelSerialToDate(dateString);
        } else {
          // Handle regular date strings like "7/16/2025"
          date = new Date(dateString);
        }

        return format(date, 'yyyy-MM');
      } catch (error) {
        console.warn(`Failed to process dateOfPayment for item ${item.id}:`, error);
        return 'unknown';
      }
    }, data);

    const sortedMonths = Object.keys(grouped)
      .filter((month) => month !== 'unknown')
      .sort();

    // Count rows per month
    const monthlyCounts: Record<string, number> = {};
    for (const ym of sortedMonths) {
      const apps = grouped[ym] || [];
      monthlyCounts[ym] = apps.length;
    }
    // Use totalRecords if provided, otherwise fall back to data.length
    const total = totalRecords !== undefined ? totalRecords : data.length;

    // Generate short labels (e.g., 'Mar', 'Apr') from months
    const monthLabels = sortedMonths.map((ym) => {
      try {
        const date = new Date(`${ym}-01`);
        return format(date, 'LLL');
      } catch (_error) {
        return ym;
      }
    });

    const chartCounts = sortedMonths.map((ym) => monthlyCounts[ym] || 0);

    // Calculate percent change from last two months
    let percentChange = 0;
    if (sortedMonths.length >= 2) {
      const prev = monthlyCounts[sortedMonths[sortedMonths.length - 2]] || 0;
      const curr = monthlyCounts[sortedMonths[sortedMonths.length - 1]] || 0;
      percentChange = prev === 0 ? 0 : ((curr - prev) / prev) * 100;
    }

    return {
      total,
      percentChange,
      monthLabels,
      chartCounts,
    };
  }, [data, totalRecords]);
}
